import { useState, useEffect, useCallback, useRef } from 'react'
import { Radio, Copy, Check, ChevronDown, Play, Square, Terminal, HelpCircle } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import './Streamer.css'

export default function Streamer() {
  const { api } = useAuth()

  // Source
  const [sourceType, setSourceType] = useState('cf') // 'cf' | 'mux' | 'catalog' | 'custom'
  const [cfVideos, setCfVideos] = useState([])
  const [muxAssets, setMuxAssets] = useState([])
  const [catalogItems, setCatalogItems] = useState([])
  const [selectedSource, setSelectedSource] = useState(null)
  const [customHlsUrl, setCustomHlsUrl] = useState('')

  // Filters
  const [cfSearch, setCfSearch] = useState('')
  const [muxSearch, setMuxSearch] = useState('')
  const [catalogSearch, setCatalogSearch] = useState('')
  const [catalogTag, setCatalogTag] = useState('')       // tag filter for the catalog source
  const [catalogLiveOnly, setCatalogLiveOnly] = useState(false) // only items in the live schedule
  const [availableTags, setAvailableTags] = useState([])
  const [tagFilter, setTagFilter] = useState('') // shared between CF and Mux
  // null = no tag filter; otherwise { streamUids: Set<string>, muxAssetIds: Set<string> }
  const [tagFilterIds, setTagFilterIds] = useState(null)

  // Destination
  const [liveInputs, setLiveInputs] = useState([])
  const [selectedInput, setSelectedInput] = useState(null)
  const [customRtmpUrl, setCustomRtmpUrl] = useState('')
  const [destType, setDestType] = useState('live-input') // 'live-input' | 'custom'
  const [protocol, setProtocol] = useState('rtmp') // 'rtmp' | 'srt'
  const [reencode, setReencode] = useState(false)  // YouTube-safe re-encode vs zero-CPU -c copy passthrough

  // Preview players
  const [showPreview, setShowPreview] = useState(false)
  const [showDestPreview, setShowDestPreview] = useState(false)
  const previewPlayerRef = useRef(null)
  const destPreviewPlayerRef = useRef(null)
  const previewUrlRef = useRef(null)
  const destPreviewUrlRef = useRef(null)

  // State
  const [status, setStatus] = useState('')
  const [copied, setCopied] = useState(false)
  const [showHelp, setShowHelp] = useState(false)

  // Load sources
  useEffect(() => {
    loadSources()
    loadLiveInputs()
    loadTags()
  }, [])

  async function loadTags() {
    try {
      const data = await api('/tags')
      setAvailableTags(data.tags || [])
    } catch {}
  }

  // When the tag filter changes, fetch both stream_uids and mux_asset_ids
  useEffect(() => {
    if (!tagFilter) { setTagFilterIds(null); return }
    let cancelled = false
    api(`/tags/filter?tag=${encodeURIComponent(tagFilter)}`)
      .then(data => {
        if (cancelled) return
        setTagFilterIds({
          streamUids: new Set(data.stream_uids || []),
          muxAssetIds: new Set(data.mux_asset_ids || []),
        })
      })
      .catch(() => { if (!cancelled) setTagFilterIds({ streamUids: new Set(), muxAssetIds: new Set() }) })
    return () => { cancelled = true }
  }, [tagFilter])

  async function loadSources() {
    try {
      const [cfData, muxData, catalogData] = await Promise.all([
        api('/videos?per_page=100').catch(() => ({ result: [] })),
        api('/mux/assets?limit=100').catch(() => ({ data: [] })),
        api('/vod').catch(() => ({ items: [] })),
      ])

      // Parse CF videos
      let cfList = []
      if (Array.isArray(cfData)) cfList = cfData
      else if (Array.isArray(cfData.result)) cfList = cfData.result
      else if (cfData.result?.videos) cfList = cfData.result.videos
      else if (cfData.videos) cfList = cfData.videos
      else if (cfData.data) cfList = cfData.data
      setCfVideos(cfList)

      setMuxAssets(muxData.data || muxData || [])

      // Content Catalog items that carry a playable HLS (CF stream_uid or an HLS media_url).
      const catList = (catalogData.items || catalogData || []).filter(
        (i) => i.stream_uid || (i.media_url && i.video_type !== 'YT')
      )
      setCatalogItems(catList)
    } catch (e) {
      setStatus(`Error loading sources: ${e.message}`)
    }
  }

  async function loadLiveInputs() {
    try {
      const data = await api('/live-inputs')
      setLiveInputs(data.liveInputs || data.result || [])
    } catch (e) {
      setStatus(`Error loading live inputs: ${e.message}`)
    }
  }

  // Fetch full live input details (list endpoint doesn't include rtmps/srt)
  async function selectLiveInput(input) {
    setSelectedInput(input) // show selection immediately
    try {
      const data = await api(`/live-inputs/${input.uid}`)
      const fullInput = data.result || data.liveInput || data
      setSelectedInput(fullInput)
      console.log('[Streamer] Full input details:', JSON.stringify(fullInput, null, 2))
    } catch (e) {
      console.error('[Streamer] Failed to fetch input details:', e)
    }
  }

  // Preview player
  const previewRef = useCallback((node) => {
    if (!node) return
    if (previewPlayerRef.current) { previewPlayerRef.current.dispose(); previewPlayerRef.current = null }
    node.innerHTML = ''
    const videoEl = document.createElement('video')
    videoEl.className = 'video-js vjs-default-skin'
    node.appendChild(videoEl)
    const player = videojs(videoEl, {
      controls: true,
      autoplay: true,
      fluid: true,
      sources: [{ src: previewUrlRef.current, type: 'application/x-mpegURL' }]
    })
    previewPlayerRef.current = player
  }, [])

  function openPreview() {
    const url = getSourceUrl()
    if (!url) { setStatus('Select a source first'); return }
    if (previewPlayerRef.current) { previewPlayerRef.current.dispose(); previewPlayerRef.current = null }
    previewUrlRef.current = url
    setShowPreview(true)
  }

  function closePreview() {
    if (previewPlayerRef.current) { previewPlayerRef.current.dispose(); previewPlayerRef.current = null }
    setShowPreview(false)
  }

  // Destination preview player
  const destPreviewRef = useCallback((node) => {
    if (!node) return
    if (destPreviewPlayerRef.current) { destPreviewPlayerRef.current.dispose(); destPreviewPlayerRef.current = null }
    node.innerHTML = ''
    const videoEl = document.createElement('video')
    videoEl.className = 'video-js vjs-default-skin'
    node.appendChild(videoEl)
    const player = videojs(videoEl, {
      controls: true,
      autoplay: true,
      fluid: true,
      sources: [{ src: destPreviewUrlRef.current, type: 'application/x-mpegURL' }]
    })
    destPreviewPlayerRef.current = player
  }, [])

  function getDestPlaybackUrl() {
    if (!selectedInput?.uid) return null
    // CF Live Input playback HLS URL
    return `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${selectedInput.uid}/manifest/video.m3u8`
  }

  function openDestPreview() {
    const url = getDestPlaybackUrl()
    if (!url) { setStatus('Select a destination first'); return }
    if (destPreviewPlayerRef.current) { destPreviewPlayerRef.current.dispose(); destPreviewPlayerRef.current = null }
    destPreviewUrlRef.current = url
    setShowDestPreview(true)
  }

  function closeDestPreview() {
    if (destPreviewPlayerRef.current) { destPreviewPlayerRef.current.dispose(); destPreviewPlayerRef.current = null }
    setShowDestPreview(false)
  }

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      if (previewPlayerRef.current) { previewPlayerRef.current.dispose(); previewPlayerRef.current = null }
      if (destPreviewPlayerRef.current) { destPreviewPlayerRef.current.dispose(); destPreviewPlayerRef.current = null }
    }
  }, [])

  // Build source HLS URL
  function getSourceUrl() {
    if (sourceType === 'custom') return customHlsUrl.trim()
    if (sourceType === 'cf' && selectedSource) {
      const uid = selectedSource.uid
      return `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${uid}/manifest/video.m3u8`
    }
    if (sourceType === 'mux' && selectedSource) {
      const pid = selectedSource.playback_ids?.[0]?.id
      return pid ? `https://stream.mux.com/${pid}.m3u8` : ''
    }
    // Content Catalog item: use the HLS bound to the item (CF stream_uid, else its media_url).
    if (sourceType === 'catalog' && selectedSource) {
      if (selectedSource.stream_uid) return `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${selectedSource.stream_uid}/manifest/video.m3u8`
      if (selectedSource.media_url) return selectedSource.media_url
      return ''
    }
    return ''
  }

  // Build destination URL
  function getDestUrl() {
    if (destType === 'custom') return customRtmpUrl.trim()
    if (selectedInput) {
      if (protocol === 'srt' && selectedInput.srt) {
        // SRT — url with streamId as passphrase parameter
        const srtUrl = selectedInput.srt.url
        const streamId = selectedInput.srt.streamId
        if (srtUrl) return streamId ? `${srtUrl}?streamid=${streamId}` : srtUrl
      }
      // RTMPS/RTMP — url + streamKey
      if (selectedInput.rtmps?.url) {
        return `${selectedInput.rtmps.url}${selectedInput.rtmps.streamKey || ''}`
      }
      if (selectedInput.rtmp?.url) {
        return `${selectedInput.rtmp.url}${selectedInput.rtmp.streamKey || ''}`
      }
    }
    return ''
  }

  // Codec args. `-c copy` = zero-CPU passthrough — correct for Cloudflare-only restreams. Re-encode =
  // YouTube-safe: forces a 2s keyframe interval (YouTube requires ≤4s — passthrough inherits the source's
  // ~5s GOP and errors) and re-encodes audio to clean AAC with timestamp resync (fixes the garbled/robotic
  // audio that -c copy carries from the segmented HLS source through to YouTube's RTMP ingest).
  function codecArgs() {
    if (!reencode) return '-c copy'
    return [
      '-c:v libx264 -preset veryfast -profile:v high -pix_fmt yuv420p',
      '-b:v 6000k -maxrate 6000k -bufsize 12000k',
      '-force_key_frames "expr:gte(t,n_forced*2)" -g 120 -keyint_min 120 -sc_threshold 0',
      '-c:a aac -b:a 160k -ar 48000 -ac 2 -af "aresample=async=1:first_pts=0"',
    ].join(' \\\n  ')
  }

  // Generate FFmpeg command
  function getFFmpegCommand() {
    const src = getSourceUrl()
    const dest = getDestUrl()
    if (!src || !dest) return ''

    const isSrt = protocol === 'srt' || dest.startsWith('srt://')
    const fmt = isSrt ? 'mpegts' : 'flv'
    return `ffmpeg -re -i "${src}" \\\n  ${codecArgs()} \\\n  -f ${fmt} \\\n  "${dest}"`
  }

  // Generate looping command (for 24/7 streaming)
  function getLoopCommand() {
    const src = getSourceUrl()
    const dest = getDestUrl()
    if (!src || !dest) return ''

    const isSrt = protocol === 'srt' || dest.startsWith('srt://')
    const fmt = isSrt ? 'mpegts' : 'flv'

    return `while true; do\n  ffmpeg -re -i "${src}" \\\n  ${codecArgs()} \\\n  -f ${fmt} \\\n  "${dest}"\n  echo "Stream ended, restarting in 5s..."\n  sleep 5\ndone`
  }

  function handleCopy(text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const ffmpegCmd = getFFmpegCommand()
  const loopCmd = getLoopCommand()
  const sourceUrl = getSourceUrl()
  const destUrl = getDestUrl()

  return (
    <div className="streamer">
      <div className="streamer-header">
        <h1><Radio size={20} /> Streamer</h1>
        <div className="streamer-header-right">
          {status && <span className="streamer-status">{status}</span>}
          <button className="btn-ghost btn-sm" onClick={() => setShowHelp(!showHelp)}>
            <HelpCircle size={14} /> {showHelp ? 'Hide' : 'Setup Guide'}
          </button>
        </div>
      </div>

      {showHelp && (
        <div className="streamer-help">
          <h3>FFmpeg Setup Guide</h3>
          <div className="streamer-help-grid">
            <div className="streamer-help-card">
              <h4>macOS</h4>
              <code>brew install ffmpeg</code>
            </div>
            <div className="streamer-help-card">
              <h4>Ubuntu / Debian</h4>
              <code>sudo apt install ffmpeg</code>
            </div>
            <div className="streamer-help-card">
              <h4>Windows</h4>
              <code>winget install ffmpeg</code>
              <p>Or download from ffmpeg.org</p>
            </div>
          </div>
          <p className="streamer-help-note">
            FFmpeg remuxes the HLS stream to RTMP/SRT without transcoding (<code>-c copy</code>).
            CPU usage is minimal — any machine can run this. The machine must stay on while streaming.<br /><br />
            <strong style={{ color: '#eab308' }}>SRT note:</strong> SRT protocol requires <code>libsrt</code> compiled into FFmpeg.
            On macOS: <code>brew install srt && brew reinstall ffmpeg</code>.
            RTMPS works out of the box and is recommended for most setups.
          </p>
        </div>
      )}

      <div className="streamer-layout">
        {/* Source Panel */}
        <div className="streamer-panel">
          <h2>Source</h2>
          <p className="streamer-hint">Select the HLS video to stream from</p>

          <div className="streamer-type-tabs">
            <button className={sourceType === 'cf' ? 'active' : ''} onClick={() => { setSourceType('cf'); setSelectedSource(null) }}>
              CF Stream
            </button>
            <button className={sourceType === 'mux' ? 'active' : ''} onClick={() => { setSourceType('mux'); setSelectedSource(null) }}>
              Mux
            </button>
            <button className={sourceType === 'catalog' ? 'active' : ''} onClick={() => { setSourceType('catalog'); setSelectedSource(null) }}>
              Content Catalog
            </button>
            <button className={sourceType === 'custom' ? 'active' : ''} onClick={() => { setSourceType('custom'); setSelectedSource(null) }}>
              Custom HLS
            </button>
          </div>

          {sourceType === 'cf' && (() => {
            const q = cfSearch.trim().toLowerCase()
            const filtered = cfVideos.filter(v => {
              if (tagFilterIds && !tagFilterIds.streamUids.has(v.uid)) return false
              if (q) {
                const name = (v.meta?.name || '').toLowerCase()
                const uid = (v.uid || '').toLowerCase()
                if (!name.includes(q) && !uid.includes(q)) return false
              }
              return true
            })
            return (
              <>
                <div className="streamer-filter-row">
                  <input
                    type="text"
                    className="streamer-filter-search"
                    placeholder="Search by title or UID…"
                    value={cfSearch}
                    onChange={e => setCfSearch(e.target.value)}
                  />
                  <select
                    className="streamer-filter-select"
                    value={tagFilter}
                    onChange={e => setTagFilter(e.target.value)}
                  >
                    <option value="">All tags</option>
                    {availableTags.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="streamer-select-list">
                  {cfVideos.length === 0 ? (
                    <div className="streamer-empty">No CF Stream videos found</div>
                  ) : filtered.length === 0 ? (
                    <div className="streamer-empty">No videos match the current filters</div>
                  ) : filtered.slice(0, 50).map(v => (
                    <div
                      key={v.uid}
                      className={`streamer-select-item ${selectedSource?.uid === v.uid ? 'selected' : ''}`}
                      onClick={() => setSelectedSource(v)}
                    >
                      <div className="streamer-select-title">{v.meta?.name || v.uid}</div>
                      <div className="streamer-select-meta">{v.duration ? formatDuration(v.duration) : ''}</div>
                    </div>
                  ))}
                </div>
              </>
            )
          })()}

          {sourceType === 'mux' && (() => {
            const q = muxSearch.trim().toLowerCase()
            const filtered = muxAssets.filter(a => {
              if (tagFilterIds && !tagFilterIds.muxAssetIds.has(a.id)) return false
              if (q) {
                const title = (a.meta?.title || '').toLowerCase()
                const passthrough = (a.passthrough || '').toLowerCase()
                const id = (a.id || '').toLowerCase()
                if (!title.includes(q) && !passthrough.includes(q) && !id.includes(q)) return false
              }
              return true
            })
            return (
              <>
                <div className="streamer-filter-row">
                  <input
                    type="text"
                    className="streamer-filter-search"
                    placeholder="Search by title or ID…"
                    value={muxSearch}
                    onChange={e => setMuxSearch(e.target.value)}
                  />
                  <select
                    className="streamer-filter-select"
                    value={tagFilter}
                    onChange={e => setTagFilter(e.target.value)}
                  >
                    <option value="">All tags</option>
                    {availableTags.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="streamer-select-list">
                  {muxAssets.length === 0 ? (
                    <div className="streamer-empty">No Mux assets found</div>
                  ) : filtered.length === 0 ? (
                    <div className="streamer-empty">No assets match the current filters</div>
                  ) : filtered.map(a => (
                    <div
                      key={a.id}
                      className={`streamer-select-item ${selectedSource?.id === a.id ? 'selected' : ''}`}
                      onClick={() => setSelectedSource(a)}
                    >
                      <div className="streamer-select-title">{a.meta?.title || a.passthrough || a.id}</div>
                      <div className="streamer-select-meta">{a.duration ? formatDuration(a.duration) : ''}</div>
                    </div>
                  ))}
                </div>
              </>
            )
          })()}

          {sourceType === 'catalog' && (() => {
            const q = catalogSearch.trim().toLowerCase()
            const filtered = catalogItems.filter(i => {
              if (q && !((i.title || '').toLowerCase().includes(q) || (i.tbtv_id || '').toLowerCase().includes(q))) return false
              if (catalogTag && !(i.tags || []).includes(catalogTag)) return false
              if (catalogLiveOnly && !(i.event_mode === 'live' && (i.event_type === 'upcoming' || i.event_type === 'live'))) return false
              return true
            })
            return (
              <>
                <div className="streamer-filter-row">
                  <input
                    type="text"
                    className="streamer-filter-search"
                    placeholder="Search catalog by title or ID…"
                    value={catalogSearch}
                    onChange={e => setCatalogSearch(e.target.value)}
                  />
                  <select
                    className="streamer-filter-select"
                    value={catalogTag}
                    onChange={e => setCatalogTag(e.target.value)}
                  >
                    <option value="">All tags</option>
                    {availableTags.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <label className="streamer-live-filter" title="Only items currently in the live schedule (upcoming/live)">
                    <input
                      type="checkbox"
                      checked={catalogLiveOnly}
                      onChange={e => setCatalogLiveOnly(e.target.checked)}
                    />
                    In live schedule
                  </label>
                </div>
                <div className="streamer-select-list">
                  {catalogItems.length === 0 ? (
                    <div className="streamer-empty">No catalog items with a playable HLS found</div>
                  ) : filtered.length === 0 ? (
                    <div className="streamer-empty">No items match the current filters</div>
                  ) : filtered.slice(0, 50).map(i => (
                    <div
                      key={i.tbtv_id}
                      className={`streamer-select-item ${selectedSource?.tbtv_id === i.tbtv_id ? 'selected' : ''}`}
                      onClick={() => setSelectedSource(i)}
                    >
                      <div className="streamer-select-title">{i.title || i.tbtv_id}</div>
                      <div className="streamer-select-meta">
                        {i.stream_uid ? 'CF' : 'HLS'}
                        {i.event_mode === 'live' ? ` · LIVE ${i.event_type || ''}`.trimEnd() : ''}
                        {i.duration ? ' · ' + formatDuration(i.duration) : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          })()}

          {sourceType === 'custom' && (
            <div className="streamer-field">
              <label>HLS URL</label>
              <input
                type="text"
                value={customHlsUrl}
                onChange={e => setCustomHlsUrl(e.target.value)}
                placeholder="https://stream.mux.com/xxx.m3u8"
              />
            </div>
          )}

          {sourceUrl && (
            <>
              <div className="streamer-url-preview">
                <label>Source URL</label>
                <code>{sourceUrl}</code>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  className="streamer-btn-test"
                  onClick={() => showPreview ? closePreview() : openPreview()}
                >
                  {showPreview ? '■ Stop Preview' : '▶ Test Source'}
                </button>
              </div>
            </>
          )}

          {showPreview && (
            <div className="streamer-preview">
              <div className="streamer-preview-header">
                <span>Source Preview</span>
                <button className="streamer-test-btn" onClick={closePreview} style={{ color: '#ef4444' }}>✕ Close</button>
              </div>
              <div ref={previewRef} className="streamer-preview-player" />
            </div>
          )}
        </div>

        {/* Destination Panel */}
        <div className="streamer-panel">
          <h2>Destination</h2>
          <p className="streamer-hint">Select where to stream to</p>

          <div className="streamer-type-tabs">
            <button className={destType === 'live-input' ? 'active' : ''} onClick={() => setDestType('live-input')}>
              Live Input
            </button>
            <button className={destType === 'custom' ? 'active' : ''} onClick={() => setDestType('custom')}>
              Custom RTMP/SRT
            </button>
          </div>

          {destType === 'live-input' && (
            <>
              <div className="streamer-select-list">
                {liveInputs.length === 0 ? (
                  <div className="streamer-empty">No live inputs configured</div>
                ) : liveInputs.map(input => (
                  <div
                    key={input.uid}
                    className={`streamer-select-item ${selectedInput?.uid === input.uid ? 'selected' : ''}`}
                    onClick={() => selectLiveInput(input)}
                  >
                    <div className="streamer-select-title">{input.meta?.name || input.uid}</div>
                    <div className="streamer-select-meta">
                      {input.rtmps ? 'RTMPS' : input.rtmp ? 'RTMP' : 'No RTMP'}{input.srt ? ' · SRT' : ''}
                    </div>
                  </div>
                ))}
              </div>
              {selectedInput && (
                <>
                  <div className="streamer-protocol-toggle">
                    <label>Protocol:</label>
                    <button className={protocol === 'rtmp' ? 'active' : ''} onClick={() => setProtocol('rtmp')}>
                      RTMP{selectedInput?.rtmps?.url ? 'S' : ''} <span style={{ fontSize: 9, opacity: 0.7, marginLeft: 4 }}>recommended</span>
                    </button>
                    <button className={protocol === 'srt' ? 'active' : ''} onClick={() => setProtocol('srt')}
                      disabled={!selectedInput?.srt}>
                      SRT
                    </button>
                  </div>
                  {protocol === 'srt' && (
                    <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#eab308', lineHeight: 1.5 }}>
                      <strong>SRT requires libsrt.</strong> Most default FFmpeg installs don't include it.<br />
                      macOS: <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 4px', borderRadius: 3, fontSize: 10, color: '#eab308' }}>brew install srt && brew reinstall ffmpeg</code><br />
                      If you get "Protocol not found", switch to RTMPS — it works out of the box.
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {destType === 'custom' && (
            <div className="streamer-field">
              <label>RTMP or SRT URL (with stream key)</label>
              <input
                type="text"
                value={customRtmpUrl}
                onChange={e => setCustomRtmpUrl(e.target.value)}
                placeholder="rtmp://live.example.com/app/stream-key"
              />
            </div>
          )}

          {destUrl && (
            <div className="streamer-url-preview">
              <label>Destination URL</label>
              <code>{destUrl.length > 80 ? destUrl.substring(0, 80) + '...' : destUrl}</code>
            </div>
          )}
          {selectedInput && !destUrl && (
            <div className="streamer-url-preview" style={{ borderColor: '#ef4444' }}>
              <label style={{ color: '#ef4444' }}>Debug: No URL resolved</label>
              <code style={{ color: '#ef4444', fontSize: 10 }}>
                rtmps: {selectedInput.rtmps ? 'exists' : 'missing'} |
                srt: {selectedInput.srt ? 'exists' : 'missing'} |
                Keys: {Object.keys(selectedInput).join(', ')}
              </code>
            </div>
          )}

          {selectedInput && destType === 'live-input' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                className="streamer-btn-test"
                onClick={() => showDestPreview ? closeDestPreview() : openDestPreview()}
                style={{ background: showDestPreview ? '#ef4444' : '#4ec5c4' }}
              >
                {showDestPreview ? '■ Stop Monitor' : '▶ Monitor Input'}
              </button>
            </div>
          )}

          {showDestPreview && (
            <div className="streamer-preview">
              <div className="streamer-preview-header">
                <span>Live Input Monitor — {selectedInput?.meta?.name || selectedInput?.uid}</span>
                <button className="streamer-test-btn" onClick={closeDestPreview} style={{ color: '#ef4444' }}>✕ Close</button>
              </div>
              <div ref={destPreviewRef} className="streamer-preview-player" />
              <div style={{ padding: '6px 12px', fontSize: 11, color: '#999', background: 'var(--color-bg, #0f0f1e)' }}>
                If no video appears, the input is not receiving a stream yet. Start FFmpeg to begin streaming.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status message when incomplete */}
      {!ffmpegCmd && (sourceUrl || destUrl) && (
        <div className="streamer-output">
          <div className="streamer-incomplete">
            {!sourceUrl && <span>Select a source video above</span>}
            {sourceUrl && !destUrl && <span>Select a destination to generate the FFmpeg command</span>}
          </div>
        </div>
      )}

      {/* Command Output */}
      {ffmpegCmd && (
        <div className="streamer-output">
          <h3><Terminal size={16} /> FFmpeg Commands</h3>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '4px 0 10px', cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={reencode} onChange={e => setReencode(e.target.checked)} style={{ marginTop: 2 }} />
            <span>
              <strong>Re-encode for YouTube compatibility</strong> (2s keyframes + clean AAC audio)
              <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginTop: 3, lineHeight: 1.5 }}>
                Turn ON when Cloudflare simulcasts this input to YouTube — fixes YouTube's “keyframe frequency”
                error and the garbled/robotic audio that <code>-c copy</code> passes through. Costs CPU (re-encoding).
                Leave OFF for Cloudflare-only restreams. Tune <code>-b:v</code> to your source resolution; on macOS you
                can swap <code>libx264</code> → <code>h264_videotoolbox</code> for hardware encoding.
              </span>
            </span>
          </label>

          <div className="streamer-cmd-section">
            <div className="streamer-cmd-header">
              <label><Play size={12} /> Single Play</label>
              <button className="btn-ghost btn-sm" onClick={() => handleCopy(ffmpegCmd)}>
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <pre className="streamer-cmd">{ffmpegCmd}</pre>
          </div>

          <div className="streamer-cmd-section">
            <div className="streamer-cmd-header">
              <label><Radio size={12} /> Loop (24/7)</label>
              <button className="btn-ghost btn-sm" onClick={() => handleCopy(loopCmd)}>
                <Copy size={12} /> Copy
              </button>
            </div>
            <pre className="streamer-cmd">{loopCmd}</pre>
          </div>

          <div className="streamer-cmd-notes">
            <p><strong>-re</strong> reads input at native frame rate (real-time playback speed)</p>
            <p><strong>-c copy</strong> remuxes without transcoding (very low CPU)</p>
            <p><strong>Loop</strong> automatically restarts when the stream ends (for VOD→live restreaming)</p>
          </div>
        </div>
      )}
    </div>
  )
}

function formatDuration(secs) {
  if (!secs) return ''
  const s = Math.round(secs)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
  return `${m}:${sec.toString().padStart(2, '0')}`
}
